import { DateSeparator } from "@/components/messages/DateSeparator";
import { isSameDay } from "@/lib/time-utils";
import { MessageRow } from "../MessageRow";
import { isMessageCompact } from "./is-message-compact";
import type { MessageEntrySource } from "@/lib/react-message-view";
import type { RenderUser } from "@/lib/render-user";
import type { WindowedItem } from "./use-windowed-items";

export type RenderedRow = {
  key: string;
  messageId: string | null;
  node: React.ReactNode;
};

interface RenderMessageRowsArgs {
  windowedItems: WindowedItem[];
  predecessorItem: WindowedItem | null;
  channelId: string;
  highlightedMessageId: string | null;
  pinnedMessageIds: Set<string>;
  activeCallMessageId: string | null;
  entrySource: MessageEntrySource;
  renderUserMap: Map<string, RenderUser>;
  workspaceMembers: { id: string; display_name: string }[];
  workspaceChannels: { id: string; name: string }[];
  activeWorkspaceMemberIds: Set<string> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reactionsMap: Map<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  signalsMap: Map<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attachmentsMap: Map<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  linkPreviewsMap: Map<string, any>;
  /** Source-message IDs that have a thread (system thread-created row).
   *  Per-row `threadActive` = `threadSourceIds.has(item.id)`. Drives the
   *  source-row side-bar accent + the "Thread • N messages" badge. */
  threadSourceIds: ReadonlySet<string>;
  onThreadClick: (threadId: string | null) => void;
  /** Discord-shape "Reply" hover action (sets replyingToMessageId in MessageArea). */
  onInlineReply?: (messageId: string) => void;
  /** Discord-shape "Create Thread" hover action. */
  onCreateThread?: (messageId: string) => void;
  /** Inline-reply citation pill click — scrolls the channel timeline
   *  to the cited source message in-place (Discord-shape). */
  onScrollToMessage?: (messageId: string) => void;
  onPin: (messageId: string) => void;
  onUserClick: (userId: string) => void;
  onSendToShell?: (messageId: string, selection?: string) => void;
  onChannelClick: (channelId: string) => void;
  onJoinCall?: (mode: "audio" | "video") => void;
  activeMessageId: string | null;
  setMessageOpen: (id: string, open: boolean) => void;
}

/**
 * Pure builder for the rendered-rows array consumed by
 * `MessageScrollContainer`. Inserts a `<DateSeparator>` whenever the
 * day flips and applies "compact" grouping (consecutive messages from
 * the same user inside a 5-minute window render without their own
 * header). Per-row child arrays come from the three stable Maps
 * (`reactionsMap` / `attachmentsMap` / `linkPreviewsMap`) — those
 * Maps preserve identity for unchanged messages, so `MessageRow`'s
 * `memo` short-circuits.
 *
 * Implemented as a pure function (not a component / not a hook) so
 * the parent retains its existing `useMemo` boundary unchanged.
 */
export function renderMessageRows(args: RenderMessageRowsArgs): RenderedRow[] {
  const {
    windowedItems,
    predecessorItem,
    channelId,
    highlightedMessageId,
    pinnedMessageIds,
    activeCallMessageId,
    entrySource,
    renderUserMap,
    workspaceMembers,
    workspaceChannels,
    activeWorkspaceMemberIds,
    reactionsMap,
    signalsMap,
    attachmentsMap,
    linkPreviewsMap,
    threadSourceIds,
    onThreadClick,
    onInlineReply,
    onCreateThread,
    onScrollToMessage,
    onPin,
    onUserClick,
    onSendToShell,
    onChannelClick,
    onJoinCall,
    activeMessageId,
    setMessageOpen,
  } = args;

  const rows: RenderedRow[] = [];
  for (let i = 0; i < windowedItems.length; i++) {
    const item = windowedItems[i];
    const prev = i > 0 ? windowedItems[i - 1] : (predecessorItem ?? null);
    const msgDate = new Date(item.created_at);
    const prevDate = prev ? new Date(prev.created_at) : null;

    if (!prevDate || !isSameDay(msgDate, prevDate)) {
      rows.push({
        key: `date-${item.id}`,
        messageId: null,
        node: <DateSeparator key={`date-${item.id}`} date={msgDate} />,
      });
    }

    // Decision lives in `is-message-compact.ts` so it can be unit-
    // tested in isolation. Carve-outs for automation-authored messages
    // (don't compact across cron jitter) live there too.
    const compact = isMessageCompact(item, prev);

    // Per-row child arrays come from the three stable Maps above.
    // `Map.get(item.id)` returns the SAME reference across pushes
    // when that message's reactions/attachments/link-previews
    // didn't change, so MessageRow's `memo` short-circuits.
    rows.push({
      key: item.id,
      messageId: item.id,
      node: (
        <MessageRow
          key={item.id}
          messageId={item.id}
          channelId={channelId}
          compact={!!compact}
          highlighted={item.id === highlightedMessageId}
          isPinned={pinnedMessageIds.has(item.id)}
          activeCall={item.id === activeCallMessageId}
          entrySource={entrySource}
          renderUserMap={renderUserMap}
          workspaceMembers={workspaceMembers}
          workspaceChannels={workspaceChannels}
          activeWorkspaceMemberIds={activeWorkspaceMemberIds}
          reactions={reactionsMap.get(item.id)}
          signals={signalsMap.get(item.id)}
          attachments={attachmentsMap.get(item.id)}
          linkPreviews={linkPreviewsMap.get(item.id)}
          threadActive={threadSourceIds.has(item.id)}
          onThreadClick={onThreadClick}
          onInlineReply={onInlineReply}
          onCreateThread={onCreateThread}
          onScrollToMessage={onScrollToMessage}
          onPin={onPin}
          onUserClick={onUserClick}
          onSendToShell={onSendToShell}
          onChannelClick={onChannelClick}
          onJoinCall={onJoinCall}
          actionPillOpen={activeMessageId === item.id}
          setMessageOpen={setMessageOpen}
        />
      ),
    });
  }

  return rows;
}
